import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Spinner, SpinnerSize } from '@fluentui/react';
import * as MailActions from '../../actions/mail';
import { useDispatch, useSelector } from 'react-redux';
import { SearchContext } from './SearchContainer';
import { setDocrioFiles, setSaveLocationAdded } from '../../actions/files';

import './SearchSubmit.scss';

const SearchSubmit = () => {
  const { checkedRecords } = useContext(SearchContext);
  const dispatch = useDispatch();

  const files = useSelector((state) => state.files.docrioFiles);
  const disableAddSaveLocation = useSelector((state) => state.files.disableAddSaveLocation);

  const emailId = useSelector((state) => state.mail.activeEmail);
  const draftId = useSelector((state) => state.mail.draftId);

  const selectEmailFilesByDefault = useSelector((state) => state.mail.settings.docrioSettings.emailFiles.selectEmailFilesByDefault);
  const saveAllEmailFiles = useSelector((state) => state.mail.settings.docrioSettings.emailFiles.saveAllEmails);
  const selectAttachmentsByDefault = useSelector((state) => state.mail.settings.docrioSettings.attachments.selectAttachmentsByDefault);
  const saveAllAttachments = useSelector((state) => state.mail.settings.docrioSettings.attachments.saveAllAttachments);
  const autoThreadingWasToggled = useSelector((state) => state.mail.autoThreadingToggled);
  const autoThreadingDefault = useSelector((state) => state.mail.autoThreadingDefault);
  const initialAutothreadedRecords = useSelector((state) => state.mail.initialAutothreadedRecords);

  const folderMaps = useSelector((state) => state.folders.folderMaps);
  const defaultFolderSettings = useSelector((state) => state.mail.settings.docrioSettings.defaultFolderSettings);
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);

  const addCheckedRecords = () => {
    setFolderPaths(checkedRecords);
    const formattedCheckedRecords = Object.values(checkedRecords || {});
    if (!autoThreadingWasToggled && isComposingEmail && !autoThreadingDefault && initialAutothreadedRecords?.length > 0) {
      formattedCheckedRecords?.filter((record) => initialAutothreadedRecords?.includes(record?.Id))
          .forEach((record) => record.autothreading = true);
    }
    dispatch(MailActions.selectRecords(formattedCheckedRecords));
    dispatch(MailActions.setIsSearchingRelatedRecords(false));
    const savedStatusObject = Object.values(checkedRecords).reduce((acc, record) => {
      acc[record.Id] = record.emailMessageRecordSaved || false;
      return acc;
    }, {});
    dispatch(MailActions.activityTimelineSaved(savedStatusObject));
  };

  const setFolderPaths = (selectedRecords) => {
    const newDocrioFiles = [];
    const newFolderPaths = {};
    let docrioEml;
    const attachments = [];

    for (const file of files) {
      if (file.id === emailId || file.id === draftId) {
        docrioEml = file;
      } else {
        attachments.push(file);
      }
    }

    Object.keys(selectedRecords).forEach((recordId) => {
      // if a folder path is already set, don't overwrite it otherwise use the default

      let defaultEmailFolderPath = defaultFolderSettings[selectedRecords[recordId].type].saveEmailFolderPath || '[]';
      if (defaultEmailFolderPath === '[]') {
        defaultEmailFolderPath = '';
      }

      if (selectEmailFilesByDefault || saveAllEmailFiles) {
        (docrioEml.folderPaths?.[recordId] && JSON.stringify(docrioEml.folderPaths[recordId]) !== '{}') ?
        newFolderPaths[recordId] = docrioEml.folderPaths[recordId] :
        newFolderPaths[recordId] = folderMaps?.[recordId]?.[defaultEmailFolderPath] || { name: JSON.parse(defaultEmailFolderPath || '[]')?.pop() || `${defaultFolderSettings[selectedRecords[recordId].type].objectLabel} Home`, path: defaultEmailFolderPath, subFolders: [] };
      }
    });
    newDocrioFiles.push({
      ...docrioEml,
      toSave: selectAttachmentsByDefault || !docrioEml.saved && docrioEml.toSave !== false,
      saved: !selectAttachmentsByDefault && docrioEml.saved,
      folderPaths: { ...docrioEml?.folderPaths, ...newFolderPaths },
    });

    attachments.forEach((attachment) => {
      // handle docrio files that were saved to a old selected record
      if (isComposingEmail && attachment.docrioId && attachment.folderPaths) {
        if (!selectedRecords[Object.keys(attachment.folderPaths)[0]]) {
          delete attachment.docrioId;
        }
      }
      // setup folderPaths object
      const newFolderPaths = { ...attachment.folderPaths };
      Object.keys(selectedRecords).forEach((recordId) => {
        // if a folder path is already set, don't overwrite it otherwise use the default

        let defaultAttachmentFolderPath = defaultFolderSettings[selectedRecords[recordId].type].saveAttachmentsFolderPath;
        if (defaultAttachmentFolderPath === '[]') {
          defaultAttachmentFolderPath = '';
        }

        if (selectAttachmentsByDefault || saveAllAttachments) {
          (attachment.folderPaths?.[recordId] && JSON.stringify(attachment.folderPaths[recordId]) !== '{}') ?
          newFolderPaths[recordId] = attachment.folderPaths[recordId] :
          newFolderPaths[recordId] = folderMaps?.[recordId]?.[defaultAttachmentFolderPath] || { name: JSON.parse(defaultAttachmentFolderPath || '[]')?.pop() || `${defaultFolderSettings[selectedRecords[recordId].type].objectLabel} Home`, path: defaultAttachmentFolderPath, subFolders: [] };
        }
      });
      newDocrioFiles.push({
        ...attachment,
        toSave: selectAttachmentsByDefault || (!attachment.saved && attachment.toSave !== false),
        saved: !selectAttachmentsByDefault && attachment.saved,
        folderPaths: { ...attachment?.folderPaths, ...newFolderPaths },
      });
    });

    dispatch(setSaveLocationAdded(Object.keys(newFolderPaths).length > Object.keys(docrioEml.folderPaths || {}).length));
    dispatch(setDocrioFiles(newDocrioFiles));
  };


  return (
    <>
      <p className='searchSubmit-header'>Saved Location</p>
      <div className="searchSubmit">
        <div className="searchSubmit-count">
          <span>Saved Location(s): &nbsp;</span> {Object.keys?.(checkedRecords || {})?.length || 0}
        </div>
        <button disabled={!Object.keys?.(checkedRecords || {})?.length || disableAddSaveLocation} className="searchSubmit-button" onClick={addCheckedRecords}>
          Add {(!!Object.keys?.(checkedRecords || {})?.length && disableAddSaveLocation) && <Spinner size={SpinnerSize.xSmall} style={{ marginLeft: '4px' }} />}
        </button>
      </div>
    </>
  );
};

SearchSubmit.propTypes = {
  checkedRecords: PropTypes.object,
};

export default SearchSubmit;
